import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, TrendingUp, TrendingDown, Calendar as CalendarIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABELS = {
  donuts: "Donuts",
  meat_pies: "Meat Pies",
  sausage_rolls: "Sausage Rolls",
  hotdog_rolls: "Hotdog Rolls",
  egg_rolls: "Egg Rolls"
};

const COST_LABELS = {
  staff_salaries: "Staff Salaries",
  raw_materials: "Raw Materials",
  utilities: "Utilities",
  fuel: "Fuel",
  vehicle_maintenance: "Vehicle Maintenance",
  miscellaneous: "Miscellaneous"
};

const PERIOD_LABELS = {
  daily: "Today",
  weekly: "This Week",
  monthly: "This Month",
  quarterly: "This Quarter",
  yearly: "This Year"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard?period=${period}`);
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/reports/export?period=${period}&format=csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${period}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report exported successfully");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  // Prepare chart data
  const salesChartData = reportData?.sales_by_product 
    ? Object.entries(reportData.sales_by_product).map(([key, value]) => ({
        name: PRODUCT_LABELS[key] || key,
        sales: value.amount,
        quantity: value.quantity
      }))
    : [];

  const costsChartData = reportData?.costs_by_category
    ? Object.entries(reportData.costs_by_category).map(([key, value]) => ({
        name: COST_LABELS[key] || key,
        amount: value
      }))
    : [];

  const isProfit = (reportData?.total_profit || 0) >= 0;

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in" data-testid="reports-loading">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 tracking-tight">Reports</h1>
          <p className="text-stone-500 mt-1">View and export financial reports</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="report-period-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={handleExport}
            disabled={exporting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="export-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Period Info */}
      <Card className="bg-stone-50 border-stone-200">
        <CardContent className="p-4 flex items-center gap-3">
          <CalendarIcon className="w-5 h-5 text-stone-500" />
          <div>
            <p className="text-sm font-medium text-stone-700">
              Report Period: {PERIOD_LABELS[period]}
            </p>
            <p className="text-xs text-stone-500">
              {reportData?.start_date?.split('T')[0]} to {reportData?.end_date?.split('T')[0]}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover" data-testid="report-total-sales">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Total Revenue</p>
                <p className="text-3xl font-bold text-stone-900 mt-2">
                  {formatCurrency(reportData?.total_sales || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="report-total-costs">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Total Expenses</p>
                <p className="text-3xl font-bold text-stone-900 mt-2">
                  {formatCurrency(reportData?.total_costs || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover ${isProfit ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`} data-testid="report-net-profit">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Net Profit</p>
                <p className={`text-3xl font-bold mt-2 ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatCurrency(reportData?.total_profit || 0)}
                </p>
                <p className="text-sm text-stone-500 mt-1">
                  Margin: {reportData?.profit_margin?.toFixed(1) || 0}%
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isProfit ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {isProfit ? (
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales by Product Chart */}
      <Card data-testid="report-sales-chart">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-stone-800">Sales by Product</CardTitle>
        </CardHeader>
        <CardContent>
          {salesChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salesChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" stroke="#78716C" fontSize={12} />
                <YAxis stroke="#78716C" fontSize={12} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === "sales" ? formatCurrency(value) : value,
                    name === "sales" ? "Revenue" : "Quantity"
                  ]}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #E7E5E4',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="sales" fill="#D97706" name="Revenue" radius={[4, 4, 0, 0]} />
                <Bar dataKey="quantity" fill="#10B981" name="Quantity" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state h-[300px]">
              <p>No sales data available for this period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Breakdown */}
        <Card data-testid="report-sales-breakdown">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-stone-800">Sales Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {salesChartData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty Sold</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesChartData.map((item) => (
                    <TableRow key={item.name}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right font-semibold text-amber-600">
                        {formatCurrency(item.sales)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-stone-50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">
                      {salesChartData.reduce((sum, item) => sum + item.quantity, 0)}
                    </TableCell>
                    <TableCell className="text-right text-amber-600">
                      {formatCurrency(reportData?.total_sales || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-stone-500">No sales data</div>
            )}
          </CardContent>
        </Card>

        {/* Costs Breakdown */}
        <Card data-testid="report-costs-breakdown">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-stone-800">Costs Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {costsChartData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costsChartData.map((item) => {
                    const percentage = reportData?.total_costs > 0 
                      ? ((item.amount / reportData.total_costs) * 100).toFixed(1)
                      : 0;
                    return (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell className="text-right text-stone-500">
                          {percentage}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-stone-50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right text-red-600">
                      {formatCurrency(reportData?.total_costs || 0)}
                    </TableCell>
                    <TableCell className="text-right">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-stone-500">No cost data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Profit Summary */}
      <Card className={isProfit ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'} data-testid="report-profit-summary">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-stone-800">Profit Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-stone-500">Total Revenue</p>
              <p className="text-xl font-bold text-stone-900">{formatCurrency(reportData?.total_sales || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Total Costs</p>
              <p className="text-xl font-bold text-red-600">- {formatCurrency(reportData?.total_costs || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Net Profit</p>
              <p className={`text-xl font-bold ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                {isProfit ? '' : '-'}{formatCurrency(Math.abs(reportData?.total_profit || 0))}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Profit Margin</p>
              <p className={`text-xl font-bold ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                {reportData?.profit_margin?.toFixed(1) || 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
